import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { groupPatientObservations } from '@eluve/fhir-observations';

import { CarePlansOverview } from './components/CarePlansOverview';
import {
  useFhirPatientId,
  usePatientCarePlans,
  usePlanConditions,
  usePlanGoals,
  usePlanObservations,
  usePlanServiceRequests,
} from './data/care-plans';
import { buildCarePlanList } from './plan-list';

/**
 * The Care Plans vertical slice for the new patient profile Overview tab
 * (ENG-5369): resolves the FHIR Patient from the route's (Hasura) patient
 * id, loads the care-plan connection plus the request/goal windows phase
 * states and goal links resolve from, and owns the section. The expanded
 * card lives in the URL (`?plan=<id>`) so the deep-dive state stays
 * deep-linkable; condition and goal cross-links write the sibling sections'
 * drawer params (`?condition=<id>`, `?goal=<id>`).
 */
const EXPANDED_PARAM = 'plan';
const CONDITION_PARAM = 'condition';
const GOAL_PARAM = 'goal';

export const PatientCarePlansSection: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const fhirPatient = useFhirPatientId(patientId);
  const carePlans = usePatientCarePlans(fhirPatient.data ?? null);
  // Failed/partial request, condition, goal, or observation reads degrade
  // the cards (phase steps fall back to the CarePlan's own activity
  // statuses; condition/goal rows to reference displays; goal rows to
  // trendless rows) — the plans themselves stay up.
  const serviceRequests = usePlanServiceRequests(fhirPatient.data ?? null);
  const conditions = usePlanConditions(fhirPatient.data ?? null);
  const goals = usePlanGoals(fhirPatient.data ?? null);
  const observations = usePlanObservations(fhirPatient.data ?? null);

  const list = useMemo(
    () => buildCarePlanList(carePlans.data ?? []),
    [carePlans.data],
  );
  const grouped = useMemo(
    () =>
      observations.data ? groupPatientObservations(observations.data) : null,
    [observations.data],
  );
  const todayIsoDate = new Date().toISOString().slice(0, 10);

  const expandedToken = searchParams.get(EXPANDED_PARAM);

  const toggleExpanded = useCallback(
    (token: string) => {
      setSearchParams(
        (params) => {
          if (params.get(EXPANDED_PARAM) === token) {
            params.delete(EXPANDED_PARAM);
          } else {
            params.set(EXPANDED_PARAM, token);
          }
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const openParam = useCallback(
    (param: string, token: string) => {
      setSearchParams(
        (params) => {
          params.set(param, token);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );
  const openCondition = useCallback(
    (token: string) => openParam(CONDITION_PARAM, token),
    [openParam],
  );
  const openGoal = useCallback(
    (token: string) => openParam(GOAL_PARAM, token),
    [openParam],
  );

  return (
    <CarePlansOverview
      list={list}
      serviceRequests={serviceRequests.data ?? []}
      conditions={conditions.data ?? []}
      goals={goals.data ?? []}
      grouped={grouped}
      todayIsoDate={todayIsoDate}
      loading={fhirPatient.loading || carePlans.loading}
      failed={fhirPatient.failed || carePlans.failed}
      paging={carePlans}
      expandedToken={expandedToken}
      onToggle={toggleExpanded}
      onOpenCondition={openCondition}
      onOpenGoal={openGoal}
    />
  );
};
